// ConfirmationModal.jsx - this handles queueing the emails in the backend
import React from "react";
import { useState, useEffect, useMemo } from "react";
import Lottie from "lottie-react";
import mailAnimation from "./mail-animation.json";
import { SendCANmail, SendAdHocCANmail, ScheduleCANmail, UpdateScheduledEmail, CancelScheduledEmail } from "api/canmail/CANmailAPI";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  Alert,
  Box,
} from "components/mui";
import SuccessModal from "./SuccessModal";
import { formatScheduleDate } from "utils/canmail/formatScheduleDate";
import { detectEmojis } from "utils/canmail/detectEmojis";
import { validateVariables } from "libs/utils/canmailVariables";
import { useNotificationProvider } from "hooks";

/**
 * One labelled line in the confirm dialog's summary.
 *
 * `action.hover` rather than a literal near-white: it is a subtle adaptive overlay, so
 * the panel stays readable in dark mode. (`background.default` would not work here —
 * this theme's light value is a blue tint, not a neutral.)
 */
function SummaryRow({ label, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        bgcolor: "action.hover",
        borderRadius: 2,
      }}
    >
      <Typography>
        <strong>{label}:</strong> {children}
      </Typography>
    </Paper>
  );
}

function ConfirmationModal({
  open,
  onClose,
  mailLists,
  selectedTo,
  recipientMode = "list",
  adHocRecipients = [],
  emailFrom,
  replyTo,
  subject,
  emailBody,
  attachments,
  scheduledDate,
  onEmailSent,
  contentText = "",
  contentDrop = "",
  // NEW: Edit mode props
  editMode = false,
  editingScheduleId = null,
  sendNowMode = false,
  scheduleId = null,
}) {
  const [sending, setSending] = useState(false);
  const { notifyError } = useNotificationProvider();
  const [successModal, setSuccessModal] = useState({
    open: false,
    emailsSucceeded: 0,
    emailsAttempted: 0,
    isScheduled: false,
    scheduleDate: null,
    mode: 'send',
  });

  useEffect(() => {
    if (open) {
      setSending(false);
      setSuccessModal({
        open: false,
        emailsSucceeded: 0,
        emailsAttempted: 0,
        isScheduled: false,
        scheduleDate: null,
        mode: 'send',
      });
    }
  }, [open]);

  // Last chance to catch a typo'd variable before it reaches recipients. The backend
  // only substitutes the three known tokens, so `[[frist_name]]` is delivered as
  // literal text. Deliberately a warning, not a block — literal brackets are legal.
  // `validateVariables` reports one error per occurrence; collapse repeats so a
  // typo used five times is listed once.
  const variableWarnings = useMemo(() => {
    const seen = new Map();
    validateVariables(emailBody).errors.forEach((error) => {
      // An unclosed `[[` can swallow the rest of the document into `variable`,
      // so truncate before using it as a key.
      const key = `${error.type}-${String(error.variable).slice(0, 64)}`;
      if (!seen.has(key)) seen.set(key, { ...error, key });
    });
    return [...seen.values()];
  }, [emailBody]);

  // Emoji frequently fail to render in Outlook (and some other clients), arriving as
  // empty boxes. Like the variable check above, this is a recommendation, not a block —
  // the user can still send. Distinct emoji, in the order they appear.
  const detectedEmojis = useMemo(() => detectEmojis(emailBody), [emailBody]);

  const handleSend = async () => {
    setSending(true);
    const startTime = Date.now();
    let success = false;
    let response;
    let errorMessage = "";

    try {
      if (editMode) {
        // UPDATE SCHEDULED EMAIL MODE
        if (sendNowMode) {
          await CancelScheduledEmail(editingScheduleId);

          response = await SendCANmail(
            Number(selectedTo),
            emailFrom,
            replyTo,
            subject,
            emailBody,
            attachments
          );
        } else {
          // `attachments` is already the complete desired list — AttachmentModal hands
          // the composer a final array rather than add/delete operations. Sending it
          // wholesale means one transactional request instead of N+1, with no indices
          // to go stale and no partial-failure window.
          const updatePayload = {
            list_id: Number(selectedTo),
            from: emailFrom,
            reply_to: replyTo,
            subject: subject,
            email_contents: emailBody,
            content_text: contentText,
            content_drop: contentDrop,
            schedule_date: scheduledDate,
            attachments: attachments,
          };

          response = await UpdateScheduledEmail(scheduleId || editingScheduleId, updatePayload);
        }
      } else if (recipientMode === "adhoc") {
        // AD-HOC MODE: send to an inline, non-persisted recipient list (immediate only).
        response = await SendAdHocCANmail(
          adHocRecipients,
          emailFrom,
          replyTo,
          subject,
          emailBody,
          attachments
        );
      } else {
        // CREATE NEW EMAIL MODE (existing logic)
        if (scheduledDate) {
          // Schedule email
          response = await ScheduleCANmail(
            Number(selectedTo),
            emailFrom,
            replyTo,
            subject,
            emailBody,
            scheduledDate,
            attachments,
            contentText,
            contentDrop
          );
        } else {
          response = await SendCANmail(
            Number(selectedTo),
            emailFrom,
            replyTo,
            subject,
            emailBody,
            attachments
          );
        }
      }
      success = true;
    } catch (error) {
      console.error("[ConfirmationModal] Failed to send/schedule/update email:", error);

      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = "Request timed out. This usually happens with large attachments. Please try again or reduce attachment size.";
      } else {
        errorMessage = error.message || "Failed to process email";
      }
    }

    // A 200 response can still mean every recipient failed to queue server-side. Treat
    // "attempted > 0 but 0 queued" as a failure so we don't show a success modal saying
    // "0 out of N" (immediate sends only — scheduled/update responses carry no counts).
    if (
      success &&
      response &&
      typeof response.emails_attempted === "number" &&
      response.emails_attempted > 0 &&
      (response.emails_queued || 0) === 0
    ) {
      success = false;
      errorMessage =
        "The email couldn't be queued for any recipient. Please try again.";
    }

    const elapsed = Date.now() - startTime;
    const delay = elapsed < 2000 ? 2000 - elapsed : 0;

    setTimeout(() => {
      setSending(false);
      onClose();

      if (success) {
        // Determine mode for SuccessModal
        // - "update": editing scheduled email (not send now)
        // - "send": sending immediately (regular send OR send now)
        // - "schedule": creating new scheduled email
        const successMode = editMode && !sendNowMode ? 'update' : sendNowMode || !scheduledDate ? 'send' : 'schedule';

        // Determine if we should show 1/1 or actual recipient counts
        // Show 1/1 for: Update mode OR creating new schedule
        // Show actual counts for: Send Now OR regular immediate send
        const isUpdateOrNewSchedule = (editMode && !sendNowMode) || (!editMode && scheduledDate);

        setSuccessModal({
          open: true,
          emailsSucceeded: isUpdateOrNewSchedule ? 1 : (response.emails_queued || 0),
          emailsAttempted: isUpdateOrNewSchedule ? 1 : (response.emails_attempted || 0),
          isScheduled: (editMode && !sendNowMode) || !!scheduledDate,
          scheduleDate: scheduledDate,
          mode: successMode,
        });

        // Defer onEmailSent (which calls resetEmailSender) to prevent blocking
        // This allows the modal to close and success modal to render first
        if (onEmailSent) {
          setTimeout(() => {
            onEmailSent();
          }, 100);
        }
      } else {
        notifyError(errorMessage);
      }
    }, delay);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          {sendNowMode
            ? "Send Email Now"
            : editMode
            ? "Update Scheduled Email"
            : scheduledDate
            ? "Confirm Scheduled Email"
            : "Confirm Send Email"}
        </DialogTitle>
        <DialogContent
          sx={{
            minWidth: 400,
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {sending ? (
            <>
              <Lottie
                animationData={mailAnimation}
                loop={true}
                style={{ height: 200, width: 200, margin: "32px auto" }}
              />
              {attachments && attachments.length > 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: -2, mb: 2 }}>
                  Processing {attachments.length} attachment{attachments.length > 1 ? 's' : ''}... This may take a moment.
                </Typography>
              )}
            </>
          ) : (
            <>
              {/* Different message based on mode */}
              {sendNowMode ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Warning:</strong> This will cancel the scheduled email and send it immediately to all recipients.
                  </Typography>
                </Alert>
              ) : editMode ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    The original schedule will be replaced with your changes.
                  </Typography>
                </Alert>
              ) : scheduledDate ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    This email will be sent on <strong>{formatScheduleDate(scheduledDate)}</strong>
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    This email will be sent <strong>immediately</strong> to all recipients.
                  </Typography>
                </Alert>
              )}

              {variableWarnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {variableWarnings.length === 1
                      ? "1 variable will not be replaced"
                      : `${variableWarnings.length} variables will not be replaced`}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {variableWarnings.map((warning) => (
                      <li key={warning.key}>
                        <Typography variant="body2" component="span">
                          {warning.message}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                  <Typography variant="body2">
                    Recipients will see this text exactly as written. You can still
                    continue.
                  </Typography>
                </Alert>
              )}

              {detectedEmojis.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    This email contains emoji
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Emoji often don&apos;t display in Outlook and some other email
                    clients, where recipients may see empty boxes instead. We recommend
                    removing them.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Found:{" "}
                    <Box component="span" sx={{ fontSize: "1.15em" }}>
                      {detectedEmojis.join(" ")}
                    </Box>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    You can still continue.
                  </Typography>
                </Alert>
              )}

              <SummaryRow label="To">
                {recipientMode === "adhoc"
                  ? `${adHocRecipients.length} recipient${
                      adHocRecipients.length === 1 ? "" : "s"
                    }`
                  : mailLists.find(
                      (list) => Number(list.list_id) === Number(selectedTo)
                    )?.list_name || "Unknown List"}
              </SummaryRow>
              <SummaryRow label="From">{emailFrom}</SummaryRow>
              <SummaryRow label="Subject">{subject}</SummaryRow>
              {attachments && attachments.length > 0 && (
                <SummaryRow label="Attachments">
                  {attachments.length} file(s)
                </SummaryRow>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button color="error" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            color="secondary"
            disabled={sending}
            sx={{ minWidth: 140, fontWeight: 600 }}
          >
            {sending
              ? "Processing..."
              : sendNowMode
              ? "Send Now"
              : editMode
              ? "Update Schedule"
              : scheduledDate
              ? "Schedule Email"
              : "Send Email"}
          </Button>
        </DialogActions>
      </Dialog>
      <SuccessModal
        open={successModal.open}
        onClose={() => setSuccessModal({ ...successModal, open: false })}
        emailsSucceeded={successModal.emailsSucceeded}
        emailsAttempted={successModal.emailsAttempted}
        isScheduled={successModal.isScheduled}
        scheduleDate={successModal.scheduleDate}
        mode={successModal.mode}
      />
    </>
  );
}

export default ConfirmationModal;
